/**
 * Compact row action icons for school-admin list tables.
 * 1) Vue pages can use <row-actions-dropdown> (renders icon + tooltip).
 * 2) Other list tables are enhanced in place: original buttons stay in the DOM
 *    (v-if / v-confirm / router-link keep working) and a row of icon buttons
 *    proxies those clicks. Extra actions collapse into a ⋯ overflow menu.
 */
import { iconSvg } from '../icons/admin-icons'

const SKIP = '.sip-home, .sip-pager, .modal, [data-sip-widget], .sip-dash-widget, .nk-header, .sip-admin-header';

const MAX_VISIBLE_ICONS = 4;

const ICON_LABELS = [
    { test: /\bni-eye(?:-fill|-alt)?\b/, key: 'general.view', fallback: 'View' },
    { test: /\bni-pen(?:cil)?(?:-fill|-alt)?\b|\bni-edit(?:-fill|-alt)?\b/, key: 'general.edit', fallback: 'Edit' },
    { test: /\bni-trash(?:-fill|-alt)?\b/, key: 'general.delete', fallback: 'Delete', danger: true },
    { test: /\bni-download(?:-fill|-cloud)?\b|\bfa-download\b|\bfas\s+fa-download\b/, key: 'general.download', fallback: 'Download' },
    { test: /\bni-printer(?:-fill)?\b/, key: 'general.print', fallback: 'Print' },
    { test: /\bni-file-pdf(?:-fill)?\b/, key: 'general.generate_pdf', fallback: 'Generate PDF' },
    { test: /\bni-reload\b|\bni-undo\b|\bni-hist(?:ory)?\b/, key: 'general.restore', fallback: 'Restore' },
    { test: /\bni-swap-v\b|\bni-swap\b/, key: 'general.reorder', fallback: 'Reorder' },
    { test: /\bni-check(?:-thick|-circle-fill)?\b/, key: 'general.complete', fallback: 'Complete' },
    { test: /\bni-cross(?:-sm|-circle)?\b/, key: 'general.cancel', fallback: 'Cancel' },
    { test: /\bni-file(?:-text)?\b/, key: 'general.view', fallback: 'View' }
];

const LABEL_TO_ICON = [
    { test: /copy|duplicate/i, name: 'copy' },
    { test: /allocat/i, name: 'layers' },
    { test: /print/i, name: 'printer' },
    { test: /download|export/i, name: 'download' },
    { test: /restore|reload|refresh/i, name: 'refresh' },
    { test: /reorder|swap/i, name: 'sort-asc' },
    { test: /^view$|view fee|detail/i, name: 'file-search' },
    { test: /^edit$/i, name: 'pencil' },
    { test: /^delete$|remove/i, name: 'trash' },
    { test: /pdf/i, name: 'file' },
    { test: /complete/i, name: 'check-circle' },
    { test: /cancel/i, name: 'close' }
];

const CLASS_TO_ICON = [
    { test: /\bni-eye|\bfa-eye/, name: 'file-search' },
    { test: /\bni-pen|\bni-edit|\bfa-pencil|\bfa-edit/, name: 'pencil' },
    { test: /\bni-trash|\bfa-trash/, name: 'trash' },
    { test: /\bni-printer|\bfa-print/, name: 'printer' },
    { test: /\bni-download|\bfa-download/, name: 'download' },
    { test: /\bni-reload|\bni-undo|\bfa-undo|\bfa-refresh/, name: 'refresh' },
    { test: /\bni-swap/, name: 'sort-asc' },
    { test: /\bni-copy|\bfa-copy|\bfa-clone/, name: 'copy' },
    { test: /\bfa-user-plus|\bni-user-add/, name: 'user-plus' },
    { test: /\bfa-lock|\bni-lock/, name: 'lock' },
    { test: /\bni-file-pdf|\bfa-file-pdf/, name: 'file' },
    { test: /\bni-file\b/, name: 'file' },
    { test: /\bfa-arrow-circle-right|\bni-arrow/, name: 'arrow-right' }
];

let uid = 0;
let openState = null;
let docBound = false;

function t(key, fallback) {
    if (window._get && window.i18n) {
        const value = window._get(window.i18n, key);
        if (value) {
            return value;
        }
    }
    return fallback;
}

function actionWord() {
    return t('general.action', 'Action');
}

function moreWord() {
    return t('general.more', 'More');
}

function iconClass(el) {
    if (!el) {
        return '';
    }
    const icon = el.querySelector && el.querySelector('.icon, .ni, em, i, [class*="fa-"]');
    const bits = [el.className || ''];
    if (icon) {
        bits.push(icon.getAttribute('class') || '');
    }
    return bits.join(' ');
}

export function actionLabel(el) {
    if (!el) {
        return '';
    }
    const titled = (el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('data-original-title') || '').trim();
    const text = ((el.textContent || '') + '').replace(/\s+/g, ' ').trim();
    if (text && text.length < 48) {
        return text;
    }
    if (titled) {
        return titled;
    }
    const cls = iconClass(el);
    for (let i = 0; i < ICON_LABELS.length; i++) {
        if (ICON_LABELS[i].test.test(cls)) {
            return t(ICON_LABELS[i].key, ICON_LABELS[i].fallback);
        }
    }
    if (/\bbtn-danger\b/.test(el.className) || /doc-action-btn-danger/.test(el.className)) {
        return t('general.delete', 'Delete');
    }
    return actionWord();
}

export function isDangerAction(el) {
    if (!el) {
        return false;
    }
    const cls = iconClass(el);
    if (/\bbtn-danger\b/.test(el.className) || /doc-action-btn-danger/.test(el.className)) {
        return true;
    }
    return /\bni-trash(?:-fill|-alt)?\b/.test(cls);
}

function resolveActionIcon(el) {
    const label = actionLabel(el);
    for (let i = 0; i < LABEL_TO_ICON.length; i++) {
        if (LABEL_TO_ICON[i].test.test(label)) {
            return LABEL_TO_ICON[i].name;
        }
    }
    const cls = iconClass(el);
    for (let i = 0; i < CLASS_TO_ICON.length; i++) {
        if (CLASS_TO_ICON[i].test.test(cls)) {
            return CLASS_TO_ICON[i].name;
        }
    }
    return 'more-horizontal';
}

function iconHtml(el) {
    return iconSvg(resolveActionIcon(el), 'sip-row-actions__glyph');
}

function actionLabelIsVisible(el) {
    const label = el && el.querySelector && el.querySelector('.sip-row-actions__btn-label');
    if (!label) {
        return false;
    }
    if (window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches) {
        return true;
    }
    if (!el.isConnected) {
        return false;
    }
    try {
        const style = window.getComputedStyle(label);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    } catch (err) {
        return false;
    }
}

function bindIconTip(el, label) {
    if (!el || !label) {
        return;
    }
    el.setAttribute('aria-label', label);
    if (actionLabelIsVisible(el)) {
        disposeTip(el);
        el.setAttribute('aria-label', label);
        return;
    }
    el.setAttribute('title', label);
    el.setAttribute('data-tip', label);
    el.dataset.sipActionTip = '1';
    if (window.$ && window.$.fn && window.$.fn.tooltip) {
        try {
            window.$(el).tooltip('dispose');
        } catch (err) { /* not inited */ }
        window.$(el).tooltip({
            container: 'body',
            placement: 'top',
            trigger: 'hover',
            title: label
        });
    }
}

function makeIconBtn(source, index) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sip-row-actions__icon-btn' + (isDangerAction(source) ? ' is-danger' : '');
    btn.setAttribute('data-sip-src-idx', String(index));
    const label = actionLabel(source);
    btn.innerHTML = iconHtml(source) + (label ? '<span class="sip-row-actions__btn-label">' + escapeHtml(label) + '</span>' : '');
    bindIconTip(btn, label);
    btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeRowActionMenu();
        try {
            if (window.$ && window.$.fn && window.$.fn.tooltip) {
                window.$(btn).tooltip('hide');
            }
        } catch (err) { /* ignore */ }
        window.setTimeout(function () {
            if (typeof source.click === 'function') {
                source.click();
            }
        }, 0);
    });
    return btn;
}

function splitSources(sources) {
    if (!sources.length) {
        return { icons: [], overflow: [] };
    }
    if (sources.length <= MAX_VISIBLE_ICONS) {
        return { icons: sources, overflow: [] };
    }
    return {
        icons: sources.slice(0, MAX_VISIBLE_ICONS - 1),
        overflow: sources.slice(MAX_VISIBLE_ICONS - 1)
    };
}

function destroyPopper(state) {
    const s = state || openState;
    if (s && s.popper && typeof s.popper.destroy === 'function') {
        try {
            s.popper.destroy();
        } catch (e) { /* already gone */ }
        s.popper = null;
    }
}

function placeMenuManual(toggle, menu) {
    const r = toggle.getBoundingClientRect();
    menu.style.position = 'fixed';
    menu.style.zIndex = '2200';
    menu.style.display = 'block';
    menu.style.visibility = 'visible';
    menu.style.transform = 'none';
    menu.style.margin = '0';
    menu.style.right = 'auto';
    const width = Math.max(menu.offsetWidth || 0, 168);
    const height = menu.offsetHeight || 0;
    let left = r.right - width;
    if (left < 8) {
        left = 8;
    }
    if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
    }
    let top = r.bottom + 4;
    if (top + height > window.innerHeight - 8 && r.top - height - 4 > 8) {
        top = r.top - height - 4;
    }
    menu.style.left = Math.round(left) + 'px';
    menu.style.top = Math.round(top) + 'px';
    menu.style.minWidth = width + 'px';
}

function attachPopper(toggle, menu) {
    destroyPopper();
    menu.style.position = 'fixed';
    menu.style.zIndex = '2200';
    menu.style.display = 'block';
    menu.style.visibility = 'visible';
    menu.style.margin = '0';
    menu.style.transform = 'none';
    menu.style.top = '0';
    menu.style.left = '0';
    const Popper = window.Popper;
    if (typeof Popper === 'function' && openState) {
        openState.popper = new Popper(toggle, menu, {
            placement: 'bottom-end',
            positionFixed: true,
            modifiers: {
                offset: { offset: '0,4' },
                flip: {
                    enabled: true,
                    behavior: ['bottom-end', 'top-end', 'bottom-start', 'top-start'],
                    padding: 8
                },
                preventOverflow: {
                    enabled: true,
                    boundariesElement: 'viewport',
                    padding: 8
                },
                hide: { enabled: false },
                computeStyle: { gpuAcceleration: false }
            }
        });
        if (typeof openState.popper.scheduleUpdate === 'function') {
            openState.popper.scheduleUpdate();
        }
        return;
    }
    placeMenuManual(toggle, menu);
}

function repositionMenu() {
    if (!openState || !openState.toggle || !openState.menu) {
        return;
    }
    if (openState.popper && typeof openState.popper.scheduleUpdate === 'function') {
        openState.popper.scheduleUpdate();
        return;
    }
    placeMenuManual(openState.toggle, openState.menu);
}

export function closeRowActionMenu() {
    if (!openState) {
        return;
    }
    const state = openState;
    openState = null;
    destroyPopper(state);
    state.toggle.setAttribute('aria-expanded', 'false');
    if (state.wrap) {
        state.wrap.classList.remove('is-open');
    }
    state.menu.classList.remove('is-open');
    state.menu.style.display = 'none';
    state.menu.style.visibility = 'hidden';
    if (state.placeholder && state.placeholder.parentNode) {
        state.placeholder.parentNode.insertBefore(state.menu, state.placeholder);
        state.placeholder.parentNode.removeChild(state.placeholder);
    }
    document.body.classList.remove('sip-row-actions-open');
}

export function toggleRowActionMenu(toggle, menu) {
    if (!toggle || !menu) {
        return;
    }
    if (openState && openState.menu === menu) {
        closeRowActionMenu();
        return;
    }
    closeRowActionMenu();
    const wrap = toggle.closest('.sip-row-actions');
    const placeholder = document.createComment('sip-row-actions-menu');
    if (menu.parentNode) {
        menu.parentNode.insertBefore(placeholder, menu);
    }
    document.body.appendChild(menu);
    menu.classList.add('is-open');
    menu.style.display = 'block';
    menu.style.visibility = 'hidden';
    if (wrap) {
        wrap.classList.add('is-open');
    }
    toggle.setAttribute('aria-expanded', 'true');
    openState = { wrap: wrap, toggle: toggle, menu: menu, placeholder: placeholder, popper: null };
    document.body.classList.add('sip-row-actions-open');
    window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
            if (!openState || openState.menu !== menu) {
                return;
            }
            menu.style.visibility = 'visible';
            attachPopper(toggle, menu);
        });
    });
}

function bindDocOnce() {
    if (docBound) {
        return;
    }
    docBound = true;
    document.addEventListener('click', function (e) {
        if (!openState) {
            return;
        }
        const t = e.target;
        if (t && t.closest && (t.closest('.sip-row-actions') || t.closest('.sip-row-actions__menu'))) {
            return;
        }
        closeRowActionMenu();
    }, true);
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
            closeRowActionMenu();
        }
    });
    document.addEventListener('scroll', repositionMenu, true);
    window.addEventListener('resize', repositionMenu);
}

export function bindRowActionTrigger(toggle, menu) {
    bindDocOnce();
    if (!toggle || toggle.dataset.sipRowBound) {
        return;
    }
    toggle.dataset.sipRowBound = '1';
    toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleRowActionMenu(toggle, menu);
    });
}

function disposeTip(el) {
    if (!el) {
        return;
    }
    try {
        if (window.$ && window.$.fn && window.$.fn.tooltip) {
            window.$(el).tooltip('hide');
            window.$(el).tooltip('dispose');
        }
    } catch (err) { /* not inited */ }
    el.removeAttribute('title');
    el.removeAttribute('data-original-title');
    el.removeAttribute('data-tip');
    if (el.dataset) {
        delete el.dataset.sipActionTip;
    }
}

function isActionHeaderText(text) {
    const v = (text || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return v === 'action' || v === 'actions';
}

function headerCells(table) {
    const row = table.querySelector('thead tr');
    return row ? Array.from(row.children) : [];
}

function actionColumnIndex(table) {
    const ths = headerCells(table);
    for (let i = 0; i < ths.length; i++) {
        if (ths[i].classList.contains('sip-th-action') || ths[i].classList.contains('nk-tb-col-tools') || ths[i].classList.contains('table-action')) {
            return i;
        }
    }
    for (let i = 0; i < ths.length; i++) {
        if (isActionHeaderText(ths[i].textContent)) {
            return i;
        }
    }
    const last = ths.length - 1;
    if (last >= 0) {
        const sample = table.querySelector('tbody tr > *:nth-child(' + (last + 1) + ')');
        if (sample && collectSources(sample).length) {
            return last;
        }
    }
    return -1;
}

function collectSources(td) {
    if (!td) {
        return [];
    }
    const list = [];
    const seen = [];
    const nodes = td.querySelectorAll('a, button');
    for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i];
        if (el.closest('.sip-row-actions')) {
            continue;
        }
        if (el.classList.contains('sip-row-actions__btn')
            || el.classList.contains('sip-row-actions__icon-btn')
            || el.classList.contains('sip-col-resizer')) {
            continue;
        }
        if (el.classList.contains('dropdown-toggle-split') || (el.classList.contains('dropdown-toggle') && el.classList.contains('btn') && el.querySelector('.ni-more-v, .ni-more-h'))) {
            continue;
        }
        if (el.closest('.sip-row-actions__menu')) {
            continue;
        }
        if (!el.closest('.btn-group, .doc-action-btns, .table-action, .nk-tb-col-tools, .sip-row-actions-src') && el.parentNode !== td && !el.classList.contains('dropdown-item')) {
            continue;
        }
        if (seen.indexOf(el) !== -1) {
            continue;
        }
        seen.push(el);
        list.push(el);
    }
    return list;
}

function sourceSignature(sources) {
    return sources.map(function (el) {
        return (el.getAttribute('aria-label') || '') + '|' + iconClass(el) + '|' + (el.textContent || '').trim();
    }).join('~');
}

function makeItem(source, index) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'sip-row-actions__item' + (isDangerAction(source) ? ' is-danger' : '');
    item.setAttribute('data-sip-src-idx', String(index));
    item.innerHTML = '<span class="sip-row-actions__icon" aria-hidden="true">' + iconHtml(source) + '</span><span class="sip-row-actions__label">' + escapeHtml(actionLabel(source)) + '</span>';
    item.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeRowActionMenu();
        window.setTimeout(function () {
            if (typeof source.click === 'function') {
                source.click();
            }
        }, 0);
    });
    return item;
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function hideSourceControls(td, sources) {
    const group = td.querySelector(':scope > .btn-group:not(.sip-row-actions), :scope > .doc-action-btns');
    if (group) {
        group.classList.add('sip-row-actions-src');
        group.setAttribute('aria-hidden', 'true');
        group.setAttribute('hidden', 'hidden');
    }
    sources.forEach(function (el) {
        disposeTip(el);
        el.setAttribute('data-sip-row-src', '1');
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('hidden', 'hidden');
    });
}

function enhanceCell(td) {
    if (!td || td.closest(SKIP)) {
        return;
    }
    if (td.querySelector(':scope > .sip-row-actions[data-sip-vue]')) {
        return;
    }
    const sources = collectSources(td);
    if (!sources.length) {
        return;
    }
    td.classList.add('table-action');
    const sig = sourceSignature(sources);
    let wrap = td.querySelector(':scope > .sip-row-actions');
    if (wrap && wrap.dataset.sipSrcSig === sig) {
        hideSourceControls(td, sources);
        return;
    }
    if (wrap && wrap.parentNode) {
        wrap.querySelectorAll('.sip-row-actions__icon-btn').forEach(disposeTip);
        wrap.parentNode.removeChild(wrap);
        wrap = null;
    }
    hideSourceControls(td, sources);
    wrap = document.createElement('div');
    wrap.className = 'sip-row-actions sip-row-actions--icons';
    wrap.dataset.sipSrcSig = sig;
    const parts = splitSources(sources);
    parts.icons.forEach(function (src, i) {
        wrap.appendChild(makeIconBtn(src, i));
    });
    if (parts.overflow.length) {
        const more = document.createElement('div');
        more.className = 'sip-row-actions__more';
        const id = 'sip-ra-' + (++uid);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sip-row-actions__icon-btn sip-row-actions__more-btn';
        btn.id = id;
        btn.setAttribute('aria-haspopup', 'menu');
        btn.setAttribute('aria-expanded', 'false');
        btn.innerHTML = iconSvg('more-horizontal', 'sip-row-actions__glyph') + '<span class="sip-row-actions__btn-label">' + escapeHtml(moreWord()) + '</span>';
        bindIconTip(btn, moreWord());
        const menu = document.createElement('div');
        menu.className = 'sip-row-actions__menu';
        menu.setAttribute('role', 'menu');
        menu.setAttribute('aria-labelledby', id);
        parts.overflow.forEach(function (src, i) {
            menu.appendChild(makeItem(src, parts.icons.length + i));
        });
        more.appendChild(btn);
        more.appendChild(menu);
        wrap.appendChild(more);
        bindRowActionTrigger(btn, menu);
    }
    td.appendChild(wrap);
}

function shouldSkipTable(table) {
    if (!table || table.matches('[data-sip-no-row-actions]')) {
        return true;
    }
    if (table.closest(SKIP)) {
        return true;
    }
    return false;
}

export function scanRowActions(root) {
    bindDocOnce();
    const scope = root && root.querySelectorAll ? root : document;
    if (scope !== document && scope.closest && scope.closest(SKIP)) {
        return;
    }
    const tables = scope.querySelectorAll
        ? scope.querySelectorAll('.table-responsive table.table, table.table')
        : [];
    tables.forEach(function (table) {
        if (shouldSkipTable(table)) {
            return;
        }
        const idx = actionColumnIndex(table);
        if (idx < 0) {
            return;
        }
        const ths = headerCells(table);
        if (ths[idx]) {
            ths[idx].classList.add('sip-th-action');
        }
        table.querySelectorAll('tbody tr').forEach(function (tr) {
            const td = tr.children[idx];
            if (td) {
                enhanceCell(td);
            }
        });
    });
}

export default {
    inserted: function (el) {
        scanRowActions(el);
    },
    componentUpdated: function (el) {
        scanRowActions(el);
    }
};
